import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Client } from '@stomp/stompjs';
import { BehaviorSubject } from 'rxjs';
import { ConfirmDialogViewModel } from 'src/app/components/confirm-dialog/confirm-dialog.view-model';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ApisEnum } from '../enums/apis.enum';
import { AppConfigService } from './app-config.service';
import { AppUpdateService } from './app-update.service';
import { FinancialService } from './financial.service';
import { StorageService as storage } from './storage.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  progressData: BehaviorSubject<any> = new BehaviorSubject<any>(null)
  hidden: BehaviorSubject<boolean> = new BehaviorSubject<any>(true)

  constructor(
    private confService: AppConfigService,
    private appUpdateService: AppUpdateService,
    private dialogService: DialogService,
    private financialService: FinancialService,
    private http: HttpClient,
    private router: Router,
    private utilService: UtilService
  ) {
    this.config = this.confService.config
  }

  /**
   * Check App Update
   * @param res Response
   */
  private checkAppUpdate(res: Response | any): Response {
    if (res.headers.has('x-appversion')) {
      this.appUpdateService.appVersion.emit(res.headers.get('x-appversion'));
    }
    return res;
  }

  /**
   * Get JWT Information
   * @param jwt JSON Web Token
   */
  getJWTInfo(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Jwt, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Revoke JWT
   * @param jwt JSON Web Token
   */
  revokeJWT(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Jwt, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Renew JWT
   * @param jwt JSON Web Token
   */
  renewJWT(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Jwt, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.put(url, {}, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Lookup Account
   * @param data Data Search
   * @param jwt JSON Web Token
   */
  lookupAccount(data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.LookupAccount, { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Statement Categories
   * @param data Data Search
   * @param jwt JSON Web Token
   */
  getStatementCategories(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.StatementsCategories, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getIncomeTypes(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Enums, 'pms', 'income-types', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getEnumCategories(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Enums, 'category', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getEnumSubCategories(filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Enums, 'subcategory', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getNews(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Partner, 'news_all', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getNextWorkDay(date, jwt?: string) {
    const url = this.utilService.makeUri(['next-workday', 'BR', { date: date }, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getFirms(data?) {
    const url = this.utilService.makeUri(['pms/firms', data, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getVersion() {
    const url = this.utilService.makeUri(['version']);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  reOptimizeIdealPortfolio(rb_account: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let model = {} as ConfirmDialogViewModel;

      this.dialogService.optimizeIdealPortfolio(rb_account).subscribe({
        next: (values) => {
          if (!values) {
            return resolve(false);
          }

          this.financialService.optimizePortfolio(storage.getClientUuid(), values, { rb_account_uuid: rb_account.uuid }).then((res: any) => {
            if (res.error) {
              const msg = `Infelizmente, algo deu errado durante a otimização da sua carteira, veja qual motivo:
                <p class="text-danger">Erro desconhecido. </p>
                Por favor, entre em contato com a central de atendimento via 
                <a href="mailto:atendimento@ladybank.com.br" target="_blank" rel="noopener noreferrer" rel="noflollow">
                atendimento@ladybank.com.br</a> ou pelos canais disponíveis no site e aplicativo.`;
              this.dialogService.showDialogError(msg);
              return resolve(false);
            }

            // apply allocation simulated to model
            const allocation_id = res['allocations'][0]['id'];
            this.financialService.applyAllocationSimulatedToModel(storage.getClientUuid(), allocation_id, { rb_account_uuid: rb_account.uuid }).then(() => {
              model.subTitle = 'Seus investimentos foram otimizados com sucesso.';
              model.msg = `Se desejar, você ainda pode fazer ajustes manuais na alocação ideal.`;
              model.txtOk = 'OK';

              this.dialogService.showDialogSuccess(model).subscribe(dialogRes => {
                resolve(!!dialogRes); // resolve com true se usuário confirmou
              });
            }).catch(() => {
              const msg = `Infelizmente, algo deu errado durante a otimização da sua carteira, veja qual motivo:
                  <p class="text-danger">Erro desconhecido. </p>
                  Por favor, entre em contato com a central de atendimento via 
                  <a href="mailto:atendimento@ladybank.com.br" target="_blank" rel="noopener noreferrer" rel="noflollow">
                  atendimento@ladybank.com.br</a> ou pelos canais disponíveis no site e aplicativo.`;
              this.dialogService.showDialogError(msg);
              resolve(false);
            });
          }).catch(error => {
            reject(error);
          });
        },
        error: (err) => reject(err)
      });
    });
  }

  // OBSOLETOS 
  async getIncomeSubCategories() {
    let incomeSubCategories: any = []
    if (storage.get('incomeSubCategories')) {
      incomeSubCategories = storage.get('incomeSubCategories')
    } else {
      incomeSubCategories = await this.getEnumSubCategories({ category: 'income' })
      storage.save('incomeSubCategories', incomeSubCategories)
    }
    return incomeSubCategories
  }

  async getExpenseCategory() {
    let expenseCategories: any = []
    if (storage.get('expenseCategories')) {
      expenseCategories = storage.get('expenseCategories')
    } else {
      expenseCategories = await this.getEnumCategories()
      storage.save('expenseCategories', expenseCategories)
    }
    return expenseCategories
  }

  async getExpenseSubCategories() {
    let expenseSubCategories: any = []
    if (storage.get('expenseSubCategories')) {
      expenseSubCategories = storage.get('expenseSubCategories')
    } else {
      expenseSubCategories = await this.getEnumSubCategories()
      storage.save('expenseSubCategories', expenseSubCategories)
    }
    return expenseSubCategories
  }
  // FIM OBSOLETOS

  async getCategories() {
    let categories: any = []
    if (storage.get('categories')) {
      categories = storage.get('categories')
    } else {
      categories = await this.getEnumCategories()
      storage.save('categories', categories)
    }
    return categories
  }

  async getSubCategories() {
    let subCategories: any = []
    if (storage.get('subCategories')) {
      subCategories = storage.get('subCategories')
    } else {
      subCategories = await this.getEnumSubCategories()
      storage.save('subCategories', subCategories)
    }
    return subCategories
  }

  private headers: any = {}
  private queue
  client: Client
  config: any
  stompIsConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null)
  stompResponse: BehaviorSubject<any> = new BehaviorSubject<any>(null)

  connectWsLina() {
    this.headers = {
      login: `${storage.getClientUuid()}@ladybank`,
      host: '/ladybank_lina_openfinance',
      passcode: storage.getClientJwt()
    }

    this.queue = `/topic/ladybank.lina.sync.${storage.getClientUuid()}`;

    // Connect Stomp Client
    if (!this.client || !this.client.connected) {
      this.client = new Client({
        brokerURL: `wss://${this.config['API_HOST']}/stomp`,
        connectHeaders: this.headers,
        onConnect: () => {
          // emmit is connected
          this.stompIsConnected.next(true);

          // topic subscribe
          this.client.subscribe(this.queue, message => {
            if (message.body) {
              console.log('stomp subscribe', message.body)
              this.stompResponse.next(message.body)
            }
          });
        },
        onDisconnect: () => {
          this.client.unsubscribe(this.queue, this.headers);
          this.stompIsConnected.next(false);
        }
      });

      // Stomp Client Activate
      this.client?.activate();

      // this.client.activate()
      storage.save('queue_active', this.queue)
    } else {
      // Subscribe Only
      this.client.subscribe(this.queue, message => {
        if (message.body) {
          console.log('stomp subscribe', message.body)
          this.stompResponse.next(message.body)
        }
      });
    }
  }

  disconnectWsLina() {
    try {
      if (this.client?.connected) {
        this.client.unsubscribe(this.queue, this.headers)
        this.client.deactivate({ force: true }).then(res => {
          this.stompIsConnected.next(false);
          this.client = null;
        }).catch(err => {
          try {
            this.client.forceDisconnect();
          } catch (e) { }
        });
      }
    } catch (_err) {
      // LoggerService.error(_err)
    }
  }
}
